import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApplicationConfigService } from '../../../core/application-config.service';
import { CommonService } from '../../../shared/services/common.service';
import { environment } from '../../../../environments/environment';
import { IEventDetails } from '../models/event-details.model';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  resourceUrl: string;
  private readonly bookingUrl = environment.services.booking;
  private readonly ticketingUrl = environment.services.ticketing;
  private readonly adminUrl = environment.services.admin;

  constructor(
    protected http: HttpClient,
    private readonly applicationConfigService: ApplicationConfigService,
    private readonly commonService: CommonService
  ) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor();
  }

  getAllEvents(query: any): Observable<any> {
    const options: any = this.commonService.toQueryString(query);
    return this.http.get<any[]>(`${this.resourceUrl}/events?${options}`);
  }

  getEventById(id: any): Observable<IEventDetails> {
    return this.http.get<any>(`${this.resourceUrl}/events/${id}`);
  }

  deleteEvent(id: number): Observable<any> {
    return this.http.delete<any>(`${this.resourceUrl}/events/${id}`);
  }

  createEvent(payload: FormData): Observable<any> {
    return this.http.post(`${this.resourceUrl}/events`, payload);
  }

  updateEventById(eventId: number, payload: FormData): Observable<any> {
    return this.http.put(`${this.resourceUrl}/events/${eventId}`, payload);
  }

  publishEvent(eventId: number) {
    return this.http.put(`${this.resourceUrl}/events/publish/${eventId}`, null);
  }

  unpublishEvent(eventId: number) {
    return this.http.put(`${this.resourceUrl}/events/unpublish/${eventId}`, null);
  }

  toggleEventStatus(eventId: number, isActive: boolean): Observable<any> {
    return this.http.put(`${this.resourceUrl}/events/toggle-status/${eventId}`, { isActive });
  }

  duplicateEvent(eventId: number): Observable<any> {
    return this.http.post(`${this.resourceUrl}/events/duplicate/${eventId}`, {});
  }

  getAllTeams(): Observable<any> {
    return this.http.get<any[]>(`${this.resourceUrl}/teams/1`);
  }

  getTeamsByProgramId(programId: number | null) {
    return this.http.get<any[]>(`${this.resourceUrl}/teams/program/${programId}`);
  }

  getAllVenues() {
    return this.http.get<any>(`${this.resourceUrl}/venues/1`);
  }

  getVenuesByProgramId(programId: number | null) {
    return this.http.get<any>(`${this.resourceUrl}/venues/${programId}`);
  }

  getGalleryData(eventId: number, isForTicket: boolean = false): Observable<any> {
    const params = new HttpParams()
      .set('isForTicket', isForTicket);
    return this.http.get<any[]>(`${this.resourceUrl}/eventsVenueDetails/${eventId}?${params}`, { observe: 'response' });
  }

  downloadBackPage(eventStringId: string) {
    return this.http.get(`${this.adminUrl}/admin/api/v1/ticket-layout/download-backpage/${eventStringId}`, {
      responseType: 'blob'
    })
  }
  getCapacityAdjustmentByEventLevel(eventId: number, levelId: number, action?: any, programId?: number, programTicketCategoryId?: number | null) {
    const query: any = {
      eventId: eventId,
      levelId: levelId,
      action: action,
    };
    if (programId) {
      query.programId = programId;
    }
    if (programTicketCategoryId) {
      query.programTicketCategoryId = programTicketCategoryId;
    }

    const options: any = this.commonService.toQueryString(query);
    return this.http.get<any[]>(`${this.bookingUrl}/booking/api/v1/event-capacity-adjustment/by-event-level?${options}`);
  }

  createCapacityAdjustment(payload: any) {
    return this.http.post(`${this.bookingUrl}/booking/api/v1/event-capacity-adjustment`, payload, {
      observe: 'response',
    });
  }

  getSeatReservationByEventLevel(payload: any, programId?: number, programTicketCategoryId?: number | null) {
    const fullPayload = { ...payload };
    if (programId) {
      fullPayload.programId = programId;
    }
    if (programTicketCategoryId) {
      fullPayload.programTicketCategoryId = programTicketCategoryId;
    }
    const options: any = this.commonService.toQueryString(fullPayload);
    return this.http.get<any[]>(`${this.bookingUrl}/booking/api/v1/seat-reservation/by-event-level?${options}`);
  }

  createSeatReservation(payload: any) {
    return this.http.post(`${this.bookingUrl}/booking/api/v1/seat-reservation`, payload, {
      observe: 'response',
    });
  }

  updatePriceByLevel(eventId: number, levelId: number, price: number) {
    return this.http.put(`${this.resourceUrl}/eventsVenueDetails/${eventId}/${levelId}/${price}`, null);
  }

  getAllPrograms() {
    return this.http.get<any>(`${this.resourceUrl}/program`);
  }

  generateTickets(eventId: any, generatePayload: any, levelId: any = null) {
    const options: any = this.commonService.toQueryString({ eventId: eventId, levelId: levelId });
    return this.http.post(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/reserve-ticket?${options}`, generatePayload);
  }

  downloadTickets(ticketNumbers: string[], fileName: string, signedToken: string): Observable<Blob> {
    const payload = {
      ticketNumbers,
      fileName,
      email: {
        recipient: '',
        subject: '',
        body: ''
      }
    };
    const encodedToken = encodeURIComponent(signedToken);

    return this.http.post(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/download-reserved?signedToken=${encodedToken}`,
      payload,
      { responseType: 'blob' }
    );
  }

  downloadTestBlankTickets(fileUrl: string, ref: string, signedToken: string): Observable<Blob> {
    const payload = {
      fileUrl,
      ref
    };
    const encodedToken = encodeURIComponent(signedToken);

    return this.http.post(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/download-test-blank-reserved?signedToken=${encodedToken}`,
      payload,
      { responseType: 'blob' }
    );
  }


  getReservedTickets(eventId: number, levelId: number): Observable<any> {
    const options: any = this.commonService.toQueryString({ eventId: eventId, levelId: levelId });
    return this.http.get(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/reserve-ticket?${options}`);
  }

  sendReservedTickets(payload: any): Observable<any> {
    const url = `${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/send-reserved`;
    return this.http.post(url, payload);
  }

  fetchSeatReservations(eventId: number, levelId: number, programId?: number, programTicketCategoryId?: number | null): Observable<any> {
    const query: any = { levelId };
    if (programId) {
      query.programId = programId;
    }
    if (programTicketCategoryId) {
      query.programTicketCategoryId = programTicketCategoryId;
    }
    const options: any = this.commonService.toQueryString(query);
    return this.http.get(`${this.bookingUrl}/booking/api/v1/seat-reservation/summary/${eventId}?${options}`);
  }

  submitMergeRequest(reservationId: string): Observable<any> {
    const params = new HttpParams()
      .set('reservationId', reservationId);

    return this.http.get(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/merge-reserved`, { params });
  }

  submitZipRequest(reservationId: string): Observable<any> {
    const params = new HttpParams()
      .set('reservationId', reservationId);

    return this.http.get(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/zip-reserved`, { params });
  }

  downloadMergeRequestData(reservationId: string, token: string): Observable<any> {
    const params = new HttpParams()
      .set('reservationId', reservationId)
      .set('signedToken', token);

    return this.http.get(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/download-merged`, {
      params,
      responseType: 'blob',
      observe: 'events',
      reportProgress: true
    });
  }
  getMergeTicketNumber(reservationId: string): Observable<any> {
    const params = new HttpParams()
      .set('reservationId', reservationId);
    return this.http.get(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/merged-ticket-number`, {
      params
    });
  }

  getZippedTicketNumber(reservationId: string): Observable<any> {
    const params = new HttpParams().set('reservationId', reservationId);
    return this.http.get(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/zipped-ticket-number`, {
      params
    });
  }
  generateSignedToken() {
    return this.http.post(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/generate-signed-token`, null, {
      responseType: 'text',
    });
  }
  setSignedCookie() {
    this.http.get(`${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/signed-cookie`, {
      withCredentials: false,
      responseType: 'text'
    })
      .subscribe({
        next: (res: string) => {
          res = res.trim();
          if (res.startsWith('"') && res.endsWith('"')) {
            res = res.substring(1, res.length - 1);
          }
          const cookieParts = res.split('; ');
          for (let part of cookieParts) {
            if (part.includes('CloudFront-')) {
              document.cookie = `${part}; path=/; domain=${environment.cloudFront.mainDomain}; Secure; SameSite=None`;
            }
          }
        }
      })
  }

  /**
   * Get available counter ticket counts
   * Adapted from DITF for RVL-ETP
   */
  getAvailableCounterTickets(request: { levelIds: number[] }): Observable<{ [levelId: number]: number }> {
    return this.http.post<{ [levelId: number]: number }>(
      `${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/counter-tickets/available-counts`,
      request
    );
  }

  /**
   * Print counter tickets - returns Base64 images
   * Adapted from DITF for RVL-ETP
   */
  printCounterTickets(request: { ticketCounts: { [levelId: number]: number } }): Observable<string[]> {
    return this.http.post<string[]>(
      `${this.ticketingUrl}/ticketing/api/v1/booking-order-ticket/counter-tickets/print`,
      request
    );
  }

  /**
   * Get current user's assigned ticket batches
   */
  getMyBatches(programId?: number, status?: number, page: number = 1, pageSize: number = 10): Observable<any> {
    let url = `${this.ticketingUrl}/ticketing/api/v1/ticket-batch/my-batches?page=${page}&pageSize=${pageSize}`;
    if (programId !== undefined && programId !== null && programId > 0) {
      url += `&programId=${programId}`;
    }
    if (status !== undefined && status !== null) {
      url += `&status=${status}`;
    }
    return this.http.get<any>(url);
  }

  /**
   * Get batch details with tickets (paginated)
   */
  getBatchDetails(batchId: number, ticketPage: number = 1, ticketPageSize: number = 100): Observable<any> {
    return this.http.get<any>(
      `${this.ticketingUrl}/ticketing/api/v1/ticket-batch/${batchId}?ticketPage=${ticketPage}&ticketPageSize=${ticketPageSize}`
    );
  }

  /**
   * Bulk print tickets from batch
   */
  bulkPrintBatchTickets(request: {
    tickets: Array<{
      batchId: number;
      ticketNumber: string;
    }>
  }): Observable<any> {
    return this.http.put<any>(
      `${this.ticketingUrl}/ticketing/api/v1/ticket-batch/print-bulk`,
      request
    );
  }

  /**
   * Print tickets from batch - Counter Print API
   */
  printBatchTickets(request: {
    batchId: number;
    ticketCount: number;
    printedBy?: string;
  }): Observable<any> {
    return this.http.post<any>(
      `${this.ticketingUrl}/ticketing/api/v1/ticket-batch/counter-print`,
      request
    );
  }

  getActivityLogs(eventId: number, query: any): Observable<any> {
    const options: any = this.commonService.toQueryString(query);
    return this.http.get<any>(`${this.adminUrl}/admin/api/v1/activity-log/event/${eventId}?${options}`);
  }
}
